import React from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useRompslompLabels } from '../../../utils/rompslompLabels';

// Accounting part of a supplier (identity, defaults, aliases). Used by the Suppliers page and by the inline
// "Add supplier" step of the expense form, so both always offer the same fields.
export const ACCOUNTING_TEXT_FIELDS = ['legal_name', 'street', 'postcode', 'city', 'country', 'kvk_number', 'vat_number', 'iban'];

export const emptyAccounting = () => ({
    legal_name: '', street: '', postcode: '', city: '', country: '', kvk_number: '', vat_number: '', iban: '',
    default_ledger_account_id: '', default_vat_treatment: '', aliases: '', notes: '',
});

export const accountingFromSupplier = (s = {}) => ({
    legal_name: s.legal_name || '', street: s.street || '', postcode: s.postcode || '', city: s.city || '', country: s.country || '',
    kvk_number: s.kvk_number || '', vat_number: s.vat_number || '', iban: s.iban || '',
    default_ledger_account_id: s.default_ledger_account_id ?? '', default_vat_treatment: s.default_vat_treatment || '',
    aliases: (s.aliases || []).join(', '), notes: s.notes || '',
});

// Values -> the API body. Empty text is sent as '' (clears the field); ids as null when empty.
export const accountingPayload = (v) => ({
    ...ACCOUNTING_TEXT_FIELDS.reduce((acc, k) => ({ ...acc, [k]: (v[k] || '').trim() }), {}),
    country: (v.country || '').trim().toUpperCase(),
    default_ledger_account_id: v.default_ledger_account_id === '' || v.default_ledger_account_id == null ? null : Number(v.default_ledger_account_id),
    default_vat_treatment: v.default_vat_treatment || null,
    aliases: (v.aliases || '').split(',').map((a) => a.trim()).filter(Boolean),
    notes: (v.notes || '').trim(),
});

export default function SupplierAccountingFields({ values, onChange, options, errors = {}, idPrefix = 'sup' }) {
    const { t } = useTranslation();
    const labels = useRompslompLabels();
    const text = (field, md = 6, extra = {}) => (
        <Col md={md} key={field}>
            <Form.Group className="mb-2" controlId={`${idPrefix}-${field}`}>
                <Form.Label>{t(`suppliers.accounting.fields.${field}`)}</Form.Label>
                <Form.Control name={field} value={values[field] ?? ''} onChange={(e) => onChange(field, e.target.value)} isInvalid={!!errors[field]} {...extra} />
                <Form.Control.Feedback type="invalid">{errors[field]}</Form.Control.Feedback>
            </Form.Group>
        </Col>
    );
    const costs = (options?.cost_accounts || []);
    const treatments = (options?.treatments || []);
    return (
        <div data-testid="supplier-accounting-fields">
            <h6 className="mb-1">{t('suppliers.accounting.heading')}</h6>
            <p className="text-muted small">{t('suppliers.accounting.help')}</p>
            <Row>
                {text('legal_name', 12)}
                {text('street', 6)}
                {text('postcode', 3)}
                {text('city', 3)}
                {text('country', 3, { maxLength: 2, placeholder: 'NL', className: 'text-uppercase' })}
                {text('kvk_number', 4)}
                {text('vat_number', 5)}
                {text('iban', 12)}
            </Row>
            <Row>
                <Col md={6}>
                    <Form.Group className="mb-2" controlId={`${idPrefix}-ledger`}>
                        <Form.Label>{t('suppliers.accounting.fields.default_ledger_account_id')}</Form.Label>
                        <Form.Select name="default_ledger_account_id" value={values.default_ledger_account_id ?? ''} onChange={(e) => onChange('default_ledger_account_id', e.target.value)}>
                            <option value="">{t('suppliers.accounting.noCategory')}</option>
                            {costs.map((a) => <option key={a.id} value={a.id}>{labels.account(a)}</option>)}
                        </Form.Select>
                        {costs.length === 0 && <Form.Text className="text-muted">{t('suppliers.accounting.noCategories')}</Form.Text>}
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-2" controlId={`${idPrefix}-treatment`}>
                        <Form.Label>{t('suppliers.accounting.fields.default_vat_treatment')}</Form.Label>
                        <Form.Select name="default_vat_treatment" value={values.default_vat_treatment || ''} onChange={(e) => onChange('default_vat_treatment', e.target.value)}>
                            <option value="">{t('suppliers.accounting.noTreatment')}</option>
                            {treatments.map((k) => <option key={k} value={k}>{t(`rompslomp.treatments.${k}`)}</option>)}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={12}>
                    <Form.Group className="mb-2" controlId={`${idPrefix}-aliases`}>
                        <Form.Label>{t('suppliers.accounting.fields.aliases')}</Form.Label>
                        <Form.Control name="aliases" value={values.aliases || ''} onChange={(e) => onChange('aliases', e.target.value)} placeholder={t('suppliers.accounting.aliasesPlaceholder')} />
                        <Form.Text className="text-muted">{t('suppliers.accounting.aliasesHelp')}</Form.Text>
                    </Form.Group>
                </Col>
                <Col md={12}>
                    <Form.Group className="mb-2" controlId={`${idPrefix}-notes`}>
                        <Form.Label>{t('suppliers.accounting.fields.notes')}</Form.Label>
                        <Form.Control as="textarea" rows={2} name="notes" value={values.notes || ''} onChange={(e) => onChange('notes', e.target.value)} />
                    </Form.Group>
                </Col>
            </Row>
        </div>
    );
}
